<script setup lang="ts">
// #803: 一覧末尾に置くセンチネル。可視領域に入ったら `load` を上げる。
//
// **スクロールコンテナは面ごとに違う** —— `/arcanelab` デスクトップと `/c` の
// デスクトップは内側の `overflow-y: auto` 要素、モバイルはウィンドウ。
// IntersectionObserver は `root: null` でも祖先の overflow によるクリップを
// 交差判定に反映するので、どちらの器でもそのまま動く（`v-infinite-scroll` は
// 自分自身がスクロールコンテナになる実装なので、この 2 系統には使えない）。
interface Props {
  hasMore: boolean
  loading: boolean
  /** 直近の取得が失敗していれば真。自動ロードを止め、再試行を出す。 */
  failed?: boolean
  /** 打ち止め時の文言。省略すると何も出さない。 */
  endText?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{ load: [], retry: [] }>()

const sentinel = ref<HTMLElement | null>(null)

// 画面下端より少し手前で先読みする（体感の「途切れ」を消す）。
//
// 交差したままロードが終わっても IntersectionObserver は再発火しない（交差状態が
// 変わらないため）。かといって `loading` の立ち下がりで無条件に読み直すと、
// **観測値がまだ古いまま**なので 1 回のスクロールで 2 ページ取ってしまう。
// そこで「発火したら pause、ロードが終わったら resume」にする。resume は対象を
// 観測し直すので、**そのときの実際の交差状態**でコールバックが再度届く:
// まだ末尾にいれば続けて取り（＝画面が埋まるまで追いかける）、離れていれば止まる。
const observer = useIntersectionObserver(
  sentinel,
  ([entry]) => {
    if (!entry?.isIntersecting) return
    if (!props.hasMore || props.loading || props.failed) return
    observer.pause()
    emit('load')
  },
  { rootMargin: '200px' },
)

watch(() => props.loading, (isLoading, wasLoading) => {
  if (wasLoading && !isLoading) observer.resume()
})
</script>

<template>
  <div class="wg-loader">
    <div
      ref="sentinel"
      class="wg-loader__sentinel"
      aria-hidden="true"
    />
    <div
      v-if="loading"
      class="wg-loader__spinner"
      role="status"
      aria-label="作品を読み込み中"
    >
      <v-progress-circular
        indeterminate
        size="28"
        width="3"
      />
    </div>
    <div
      v-else-if="failed"
      class="wg-loader__error"
      role="alert"
    >
      <span>作品の読み込みに失敗しました。</span>
      <v-btn
        size="small"
        variant="text"
        @click="emit('retry')"
      >
        再試行
      </v-btn>
    </div>
    <p
      v-else-if="!hasMore && endText"
      class="wg-loader__end"
    >
      {{ endText }}
    </p>
  </div>
</template>

<style scoped>
.wg-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* センチネルが常に可視のままにならないよう、最低限の高さを持たせる。 */
  min-height: 8px;
}

.wg-loader__sentinel {
  width: 100%;
  height: 1px;
}

.wg-loader__spinner {
  padding: 16px 0;
}

.wg-loader__error {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 0 16px;
  font-size: 0.85rem;
}

.wg-loader__end {
  padding: 12px 0 16px;
  margin: 0;
  font-size: 0.8rem;
  opacity: 0.7;
}
</style>
